/**
 * JOB HUNTER › AGENTS — /job-hunter/agents
 *
 * The active candidate's search missions. Each agent has its own CV, roles,
 * countries and bar; they all run together (or one by one from here). Every
 * agent screens every new job, but a job becomes at most one match per
 * candidate — whichever agent finds it first.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bot, FileText, Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { JH, JobHunterShell, useAgentRun, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { AgentCard } from '@/components/job-hunter/AgentCard';
import { AgentEditor } from '@/components/job-hunter/AgentEditor';
import { Notice } from '@/components/job-hunter/ui';
import { refreshAgents, upsertAgentInStore, useJobHunter } from '@/lib/job-hunter/store';
import { jobHunterService, type JhAgent, type JhCv } from '@/services/job-hunter.service';

export default function JhAgents() {
  const { profile, profileUid, agents } = useJobHunter();
  const [cvs, setCvs] = useState<JhCv[]>([]);
  const [editing, setEditing] = useState<JhAgent | null | 'new'>(null);
  const [runningUid, setRunningUid] = useState<string | null>(null);
  const { running, start } = useAgentRun();

  const loadCvs = useCallback(async () => {
    if (!profileUid) return;
    try { setCvs(await jobHunterService.cvs(profileUid)); } catch { setCvs([]); }
  }, [profileUid]);
  useEffect(() => { void loadCvs(); void refreshAgents(); }, [loadCvs]);
  useRunFinished(() => { void refreshAgents(); });
  useEffect(() => { if (!running) setRunningUid(null); }, [running]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast.success(ok); await refreshAgents(); } catch { /* toasted */ }
  };
  const run = (a: JhAgent) => { setRunningUid(a.agentUid); void start([a.agentUid], a.name); };

  const list = agents ?? [];
  return (
    <JobHunterShell section="Agents" description="Search missions — each with its own CV, roles and countries"
      actions={profileUid ? <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => setEditing('new')}>New agent</Button> : undefined}>
      {profileUid && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="jh-muted max-w-3xl">
              Agents of <b className="text-text-primary">{profile?.name}</b>. Every active agent screens every new job with its own filters and scores it with its own CV.
              A job becomes one match per candidate — whichever agent finds it first — so running several agents never doubles your inbox.
            </p>
          </div>
          {!cvs.length && list.length > 0 && (
            <Notice tone="warning" title="No CV in the library yet" action={<Link to={JH.candidate(profileUid, 'cvs')}><Button size="sm" variant="secondary" iconLeft={<FileText size={13} />}>Add a CV</Button></Link>}>
              Agents score jobs much better with a CV. Upload one (or a few, one per kind of role) and pick it per agent.
            </Notice>
          )}
          {agents === null ? (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-surface-alt" />)}</div>
          ) : list.length === 0 ? (
            <div className="jh-hero text-center">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary-soft text-primary"><Bot size={26} /></span>
              <div className="mt-3 text-[16px] font-semibold">No agents for {profile?.name} yet</div>
              <p className="jh-muted mx-auto mt-1 max-w-lg">Create an agent per kind of job you want — e.g. “Flutter · EU & UK” with your mobile CV and “Backend · Canada & Australia” with your backend CV. They run side by side.</p>
              <Button className="mt-4" iconLeft={<Plus size={14} />} onClick={() => setEditing('new')}>Create the first agent</Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {list.map((a) => (
                <AgentCard key={a.agentUid} agent={a} profileUid={profileUid} running={running && (runningUid === a.agentUid || !runningUid)}
                  onRun={() => run(a)}
                  onEdit={() => setEditing(a)}
                  onDuplicate={() => act(() => jobHunterService.duplicateAgent(a.agentUid), 'Copy created (paused) — edit it and switch it on')}
                  onDelete={() => { if (window.confirm(`Delete the agent “${a.name}”? Its matches stay; it stops screening new jobs.`)) void act(() => jobHunterService.deleteAgent(a.agentUid), 'Agent deleted'); }}
                  onToggle={(v) => act(async () => upsertAgentInStore(await jobHunterService.updateAgent(a.agentUid, { active: v })), v ? 'Agent on' : 'Agent paused')} />
              ))}
              <button type="button" onClick={() => setEditing('new')}
                className="grid min-h-[200px] place-items-center rounded-2xl border-2 border-dashed border-border text-text-muted transition hover:border-primary hover:text-primary">
                <span className="flex flex-col items-center gap-2 text-[13px] font-semibold"><Plus size={22} />New agent</span>
              </button>
            </div>
          )}
          <AgentEditor open={editing !== null} agent={editing === 'new' ? null : editing} profileUid={profileUid} cvs={cvs}
            onClose={() => setEditing(null)} onSaved={(a) => { upsertAgentInStore(a); void refreshAgents(); }} />
        </>
      )}
    </JobHunterShell>
  );
}
